import { useState, useCallback } from "react";
import { useUpdateSmtpConfiguration, type SmtpConfigurationInput } from "@/hooks/useUpdateSmtpConfiguration";
import { useTestSmtpConfiguration } from "@/hooks/useTestSmtpConfiguration";
import { useSmtpValidation } from "../hooks/useSmtpValidation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Mail, Save } from "lucide-react";
import TestEmailDialog from "./TestEmailDialog";

const AUTH_OPTIONS = [
  { value: "plain", label: "Plain" },
  { value: "login", label: "Login" },
  { value: "cram_md5", label: "CRAM-MD5" },
];

const ENCRYPTION_OPTIONS = [
  { value: "none", label: "Nenhuma" },
  { value: "starttls", label: "STARTTLS" },
  { value: "ssl_tls", label: "SSL/TLS" },
];

interface SmtpConfigData {
  id: string;
  address: string;
  port: number;
  domain: string | null;
  userName: string;
  authentication: string;
  encryption: string;
  senderEmail: string;
  senderName: string | null;
}

interface SmtpConfigurationFormProps {
  initialData: SmtpConfigData | null;
  onSaved: () => void;
  isQueryLoading: boolean;
}

export default function SmtpConfigurationForm({ initialData, onSaved, isQueryLoading }: SmtpConfigurationFormProps) {
  const [address, setAddress] = useState(initialData?.address || "");
  const [port, setPort] = useState(String(initialData?.port || 587));
  const [domain, setDomain] = useState(initialData?.domain || "");
  const [userName, setUserName] = useState(initialData?.userName || "");
  const [password, setPassword] = useState("");
  const [authentication, setAuthentication] = useState(initialData?.authentication || "plain");
  const [encryption, setEncryption] = useState(initialData?.encryption || "starttls");
  const [senderEmail, setSenderEmail] = useState(initialData?.senderEmail || "");
  const [senderName, setSenderName] = useState(initialData?.senderName || "");
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const { validate } = useSmtpValidation(initialData);

  const { updateSmtpConfiguration, loading: saving } = useUpdateSmtpConfiguration({
    onCompleted: () => {
      toast.success("Configuração SMTP salva com sucesso.");
      onSaved();
    },
    onError: (error) => {
      const graphQLError = error.graphQLErrors?.[0];
      if (graphQLError?.extensions?.field_errors) {
        const fieldErrors: Record<string, string> = {};
        Object.entries(graphQLError.extensions.field_errors as Record<string, unknown>).forEach(([key, val]) => {
          fieldErrors[key] = String(val);
        });
        setFormErrors(fieldErrors);
      }
    },
  });

  const { testSmtpConfiguration, loading: testing } = useTestSmtpConfiguration();

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      const errors = validate({
        address,
        port,
        userName,
        password,
        authentication,
        encryption,
        senderEmail,
        senderName,
      });

      setFormErrors(errors);
      if (Object.keys(errors).length > 0) return;

      const input: SmtpConfigurationInput = {
        address,
        port: parseInt(port, 10),
        domain: domain || undefined,
        userName,
        authentication,
        encryption,
        senderEmail,
        senderName: senderName || undefined,
      };

      if (password.trim()) {
        input.password = password;
      }

      updateSmtpConfiguration(input);
    },
    [
      validate,
      address,
      port,
      domain,
      userName,
      password,
      authentication,
      encryption,
      senderEmail,
      senderName,
      updateSmtpConfiguration,
    ]
  );

  const isLoading = isQueryLoading || saving || testing;

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl space-y-8">
      <div className="border border-primary-200 dark:border-primary-800 bg-white dark:bg-primary-900 p-8 space-y-6">
        <div className="flex items-center gap-3 mb-6">
          <Mail className="h-5 w-5 text-primary-600 dark:text-primary-400" />
          <h2 className="text-lg font-light tracking-tight text-primary-950 dark:text-white uppercase tracking-widest">
            Servidor de E-mail
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="smtp-address" className="text-xs uppercase tracking-widest text-primary-600 dark:text-primary-400">
              Endereço do Servidor
            </Label>
            <Input
              id="smtp-address"
              type="text"
              placeholder="smtp.example.com"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              disabled={isLoading}
              className="rounded-none border-primary-200 dark:border-primary-800"
            />
            {formErrors.address && (
              <p className="text-xs text-red-600 dark:text-red-400">{formErrors.address}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="smtp-port" className="text-xs uppercase tracking-widest text-primary-600 dark:text-primary-400">
              Porta
            </Label>
            <Input
              id="smtp-port"
              type="number"
              placeholder="587"
              value={port}
              onChange={(e) => setPort(e.target.value)}
              disabled={isLoading}
              className="rounded-none border-primary-200 dark:border-primary-800"
            />
            {formErrors.port && (
              <p className="text-xs text-red-600 dark:text-red-400">{formErrors.port}</p>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="smtp-domain" className="text-xs uppercase tracking-widest text-primary-600 dark:text-primary-400">
            Domínio
          </Label>
          <Input
            id="smtp-domain"
            type="text"
            placeholder="example.com"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            disabled={isLoading}
            className="rounded-none border-primary-200 dark:border-primary-800"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="smtp-userName" className="text-xs uppercase tracking-widest text-primary-600 dark:text-primary-400">
              Nome de Usuário
            </Label>
            <Input
              id="smtp-userName"
              type="text"
              placeholder="user@example.com"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              disabled={isLoading}
              className="rounded-none border-primary-200 dark:border-primary-800"
            />
            {formErrors.user_name && (
              <p className="text-xs text-red-600 dark:text-red-400">{formErrors.user_name}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="smtp-password" className="text-xs uppercase tracking-widest text-primary-600 dark:text-primary-400">
              Senha {initialData && "(deixe em branco para manter)"}
            </Label>
            <Input
              id="smtp-password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              className="rounded-none border-primary-200 dark:border-primary-800"
            />
            {formErrors.password && (
              <p className="text-xs text-red-600 dark:text-red-400">{formErrors.password}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-primary-600 dark:text-primary-400">
              Autenticação
            </Label>
            <Select
              value={authentication}
              onValueChange={setAuthentication}
              disabled={isLoading}
            >
              <SelectTrigger className="rounded-none border-primary-200 dark:border-primary-800">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AUTH_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {formErrors.authentication && (
              <p className="text-xs text-red-600 dark:text-red-400">{formErrors.authentication}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-primary-600 dark:text-primary-400">
              Criptografia
            </Label>
            <Select
              value={encryption}
              onValueChange={setEncryption}
              disabled={isLoading}
            >
              <SelectTrigger className="rounded-none border-primary-200 dark:border-primary-800">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ENCRYPTION_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {formErrors.encryption && (
              <p className="text-xs text-red-600 dark:text-red-400">{formErrors.encryption}</p>
            )}
          </div>
        </div>
      </div>

      <div className="border border-primary-200 dark:border-primary-800 bg-white dark:bg-primary-900 p-8 space-y-6">
        <div className="flex items-center gap-3 mb-6">
          <Mail className="h-5 w-5 text-primary-600 dark:text-primary-400" />
          <h2 className="text-lg font-light tracking-tight text-primary-950 dark:text-white uppercase tracking-widest">
            Remetente
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="smtp-senderEmail" className="text-xs uppercase tracking-widest text-primary-600 dark:text-primary-400">
              E-mail do Remetente
            </Label>
            <Input
              id="smtp-senderEmail"
              type="email"
              placeholder="noreply@example.com"
              value={senderEmail}
              onChange={(e) => setSenderEmail(e.target.value)}
              disabled={isLoading}
              className="rounded-none border-primary-200 dark:border-primary-800"
            />
            {formErrors.sender_email && (
              <p className="text-xs text-red-600 dark:text-red-400">{formErrors.sender_email}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="smtp-senderName" className="text-xs uppercase tracking-widest text-primary-600 dark:text-primary-400">
              Nome do Remetente
            </Label>
            <Input
              id="smtp-senderName"
              type="text"
              placeholder="Marq App"
              value={senderName}
              onChange={(e) => setSenderName(e.target.value)}
              disabled={isLoading}
              className="rounded-none border-primary-200 dark:border-primary-800"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <TestEmailDialog
          onTest={testSmtpConfiguration}
          testing={testing}
          disabled={isLoading}
        />
        <Button
          type="submit"
          disabled={isLoading}
          className="rounded-none uppercase tracking-widest text-xs px-8 py-3"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? "Salvando..." : "Salvar Configuração"}
        </Button>
      </div>
    </form>
  );
}
